/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Landing from '../screens/Landing';
import { Sidebar } from '@/ui/components/Sidebar';
import Dashboard from '../screens/Dashboard';
import { ProjectsList } from '../screens/ProjectsList';
import { QuickPhotoEstimate } from '../screens/QuickPhotoEstimate';
import { Discounts } from '../screens/Discounts';
import { PublicQuote } from '../screens/PublicQuote';
import { BetaPrivacyPage, BetaTermsPage, ContactPage } from '../screens/PublicInfoPages';
import ClientOffer from '../screens/ClientOffer';
import { Tasks } from '../screens/Tasks';
import MapView from '../screens/MapView';
import { AppProvider, useAppContext, type Project } from '@/store/AppContext';
import { ErrorBoundary } from '@/ui/components/ErrorBoundary';
import { ProWycenaScreen } from '../screens/ProWycenaScreen';
import { HomeChoice } from '../screens/HomeChoice';
import { Button } from '@/ui/components/ui/button';
import { Logo } from '@/ui/components/Logo';
import { Menu, X } from 'lucide-react';
import { AIChatAssistant } from '@/ui/components/AIChatAssistant';
import { useAIChat } from '@/hooks/useAIChat';
import { isLocalDevProjectFallbackEnabled } from '@/lib/localDevProjects';
import {
  clearQuickActiveDraft,
  getBrowserQuickDraftStorage,
  resolveQuickDraftOwner,
} from '@/domain/quickActiveDraft';
import { OfflineBanner } from '@/ui/components/OfflineBanner';
import { PwaUpdatePrompt } from '@/ui/components/PwaUpdatePrompt';
import { AboutProductPage, FaqPage, MarginShieldPage } from '../screens/ProductTruthPages';
import { FAQ_ENTRIES, resolveSeoRoute, SEO_CANONICAL_URL } from '@/lib/seoRoutes';
import { R1_RELEASE_FLAGS } from '@/config/releaseFlags';
import { resolveLegacyAppRedirect } from '@/lib/legacyRoutes';

const Settings = lazy(() => import('../screens/Settings').then((module) => ({ default: module.Settings })));

function upsertMetaByName(name: string, content: string): void {
  let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function upsertMetaByProperty(property: string, content: string): void {
  let meta = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function upsertCanonical(href: string): void {
  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = href;
}

function removeCanonical(): void {
  document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.remove();
}

function isTechnicalHost(hostname: string): boolean {
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1') return false;
  return hostname !== 'prowycenaapp.pl' && hostname !== 'www.prowycenaapp.pl';
}

function SeoIndexGuard() {
  const location = useLocation();

  useEffect(() => {
    const seo = resolveSeoRoute(location.pathname);
    const robots = isTechnicalHost(window.location.hostname) ? 'noindex, nofollow' : seo.robots;

    document.title = seo.title;
    upsertMetaByName('description', seo.description);
    upsertMetaByName('robots', robots);
    upsertMetaByProperty('og:title', seo.title);
    upsertMetaByProperty('og:description', seo.description);
    upsertMetaByName('twitter:title', seo.title);
    upsertMetaByName('twitter:description', seo.description);

    if (seo.canonical && seo.robots === 'index, follow') {
      upsertMetaByProperty('og:url', seo.canonical);
      upsertCanonical(seo.canonical);
    } else {
      upsertMetaByProperty('og:url', window.location.href);
      removeCanonical();
    }

    document.getElementById('prowycena-faq-schema')?.remove();
    if (location.pathname === '/faq') {
      const script = document.createElement('script');
      script.id = 'prowycena-faq-schema';
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQ_ENTRIES.map(({ question, answer }) => ({
          '@type': 'Question',
          name: question,
          acceptedAnswer: { '@type': 'Answer', text: answer },
        })),
      });
      document.head.appendChild(script);
    }
  }, [location.pathname]);

  return null;
}

function FloatingAIChat({ userName, userRole }: { userName?: string; userRole?: string | null }) {
  const aiChat = useAIChat(userName, userRole);

  return (
    <AIChatAssistant
      isOpen={aiChat.isOpen}
      isMinimized={aiChat.isMinimized}
      messages={aiChat.messages}
      input={aiChat.input}
      isLoading={aiChat.isLoading}
      aiStatus={aiChat.aiStatus}
      aiStatusReason={aiChat.aiStatusReason}
      aiProvider={aiChat.aiProvider}
      onToggle={aiChat.toggle}
      onMinimize={aiChat.minimize}
      onClose={aiChat.close}
      onSend={aiChat.handleSend}
      onInputChange={aiChat.handleInputChange}
    />
  );
}

function PublicQuickEstimateRoute() {
  return <Navigate to="/app/wycena" replace />;
}

function BetaHoldPage({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  const navigate = useNavigate();

  return (
    <div className="flex min-h-[calc(100vh-72px)] items-center justify-center px-4 py-10">
      <section className="max-w-2xl rounded-[28px] border border-[#2a2f38] bg-[#15191f] p-6 text-center shadow-2xl shadow-black/30">
        <p className="text-xs font-black uppercase tracking-[0.22em] text-amber-300">Wersja beta</p>
        <h1 className="mt-3 text-3xl font-black tracking-tight text-white">{title}</h1>
        <p className="mt-3 text-base font-semibold leading-7 text-slate-300">{description}</p>
        <button
          type="button"
          onClick={() => navigate('/app/wycena')}
          className="mt-6 min-h-14 rounded-2xl bg-[#00e676] px-6 text-base font-black text-black transition hover:bg-emerald-300"
        >
          Przejdź do szybkiej wyceny
        </button>
      </section>
    </div>
  );
}

function AppContent() {
  // All hooks must be at the top - before any conditional logic
  const [openedProjectData, setInitialProjectData] = useState<Project | null>(null);
  // Bumped by the explicit "Nowa wycena" action to remount Quick with a clean state.
  const [newEstimateKey, setNewEstimateKey] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user, isAuthReady, isAllowed, login, logoutUser, projects, userProfile } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const requestedProjectId = new URLSearchParams(location.search).get('project');
  const initialProjectData = requestedProjectId
    ? (openedProjectData?.id === requestedProjectId ? openedProjectData : projects.find((project) => project.id === requestedProjectId) ?? null)
    : null;
  const isPublicQuickEstimatePath = location.pathname === '/app/wycena' || location.pathname === '/app/wycena/';
  // Freeze only while editing. A later visit must use the latest saved project.
  useEffect(() => {
    if (!isPublicQuickEstimatePath) {
      setInitialProjectData(null);
    } else if (initialProjectData && openedProjectData !== initialProjectData) {
      setInitialProjectData(initialProjectData);
    }
  }, [initialProjectData, openedProjectData, isPublicQuickEstimatePath]);
  // Quick Estimate is public for the first test flow. Other /app routes remain protected.
  const isPublicPath = isPublicQuickEstimatePath;
  const allowLocalDevProjectFallback = isLocalDevProjectFallbackEnabled();

  // Legacy in-app routes redirect before any auth handling, so a logged-out user
  // hitting /app/quick-estimate lands on /app/wycena instead of the login guard.
  const legacyRedirect = resolveLegacyAppRedirect(location.pathname);
  if (legacyRedirect) {
    return <Navigate to={legacyRedirect} replace />;
  }

  if (!isAuthReady) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Explicit "Nowa wycena": forget this owner's local draft, detach any reopened
  // project and remount Quick. Plain navigation back to /app/wycena restores the draft.
  const startNewEstimate = (): void => {
    clearQuickActiveDraft(getBrowserQuickDraftStorage(), resolveQuickDraftOwner(user?.uid));
    setInitialProjectData(null);
    setNewEstimateKey((current) => current + 1);
    navigate('/app/wycena', { replace: true });
  };

  if (!user && isPublicQuickEstimatePath) {
    return (
      <QuickPhotoEstimate
        key={`new-${newEstimateKey}`}
        publicMode
        onNewEstimate={startNewEstimate}
        onAdvanced={() => navigate('/login')}
        onProjects={() => navigate('/login')}
      />
    );
  }

  // Auth Guard - keep /app routes behind login.
  if (!user && !isPublicPath && !allowLocalDevProjectFallback) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="text-center space-y-6 p-8 bg-white rounded-2xl shadow-sm max-w-md w-full border border-slate-100">
          <div className="flex justify-center">
            <Logo variant="dark" />
          </div>
          <div>
            <p className="text-slate-500 mt-2">Zaloguj się, aby uzyskać dostęp do swoich wycen i bazy wiedzy.</p>
          </div>
          <Button onClick={login} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg">
            Zaloguj się przez Google
          </Button>
          <Button variant="ghost" onClick={() => navigate('/')} className="w-full text-slate-400">
            Powrót do strony głównej
          </Button>
        </div>
      </div>
    );
  }

  // Get current path for sidebar highlighting - use the hook result here
  const currentPath = location.pathname;
  const handleSidebarNavigate = (path: string): void => {
    if (path === '/app/wycena' && initialProjectData) {
      // Leaving a reopened project through the menu is the explicit "Nowa wycena"
      // action. Reopening a project never clears it; only this user action
      // detaches the current project ID and starts a clean estimate.
      startNewEstimate();
    }
    navigate(path);
    setIsSidebarOpen(false);
  };

  const routeFallback = (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 text-sm font-semibold text-slate-500">
      Ładowanie...
    </div>
  );

  const appRoutes = (
    <Routes>
      <Route path="" element={<Navigate to="wycena" replace />} />
      <Route path="wycena" element={
        requestedProjectId && !initialProjectData ? (
          <div className="p-6 text-slate-200" role="status">
            <p>Oczekiwanie na zapisany projekt. Jeśli projekt się nie otwiera, sprawdź połączenie i wróć do listy projektów.</p>
            <Button className="mt-4 h-14" onClick={() => navigate('/app/projects')}>Moje projekty</Button>
          </div>
        ) : (
        <QuickPhotoEstimate
          key={initialProjectData?.id ?? `new-${newEstimateKey}`}
          onNewEstimate={startNewEstimate}
          onAdvanced={() => navigate('/app/wycena')}
          onProjects={() => navigate('/app/projects')}
          initialProject={initialProjectData}
          onProjectSaved={(id) => navigate(`/app/wycena?project=${encodeURIComponent(id)}`, { replace: true })}
        />
        )
      } />
      <Route path="dashboard" element={
        <BetaHoldPage
          title="Dashboard będzie dostępny po zakończeniu testów beta."
          description="Na pierwsze testy prowadzimy wykonawcę jednym flow: obmiar, zakres, stawka, Tarcza Marży, podsumowanie i PDF."
        />
      } />
      <Route path="projects" element={
        <ProjectsList onEditProject={(project) => {
          setInitialProjectData(project);
          navigate(`/app/wycena?project=${encodeURIComponent(project.id)}`);
        }} />
      } />
      <Route path="tasks" element={<Tasks />} />
      <Route path="map" element={<MapView projects={projects} onClose={() => navigate('/app/dashboard')} onProjects={() => navigate('/app/projects')} />} />
      <Route path="new-project" element={
        <BetaHoldPage
          title="Zaawansowany kreator jest eksperymentalny."
          description="Ten ekran zostaje technicznie w aplikacji, ale nie jest domyślnym flow pierwszych testów. Użyj szybkiej wyceny MVP."
        />
      } />
      <Route path="quick-estimate" element={<Navigate to="/app/wycena" replace />} />
      <Route path="new-project-advanced" element={<Navigate to="/app/wycena" replace />} />
      <Route path="discounts" element={<Discounts />} />
      <Route path="prowycena" element={<ProWycenaScreen />} />
      <Route path="settings" element={
        <Suspense fallback={routeFallback}>
          <Settings />
        </Suspense>
      } />
      <Route path="*" element={<Navigate to="" replace />} />
    </Routes>
  );

  return (
    <div className="relative flex h-screen overflow-hidden bg-[#0b0d10] font-sans text-slate-100">
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar Container */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-[292px] transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:pointer-events-auto
        ${isSidebarOpen ? 'translate-x-0 pointer-events-auto' : '-translate-x-full pointer-events-none'}
      `}>
        <Sidebar 
          currentPath={currentPath} 
          onNavigate={handleSidebarNavigate}
        />
      </div>

      <main className="relative z-0 flex-1 overflow-y-auto bg-[#0b0d10]">
        {/* Mobile Header */}
        <div className="sticky top-0 z-30 flex items-center justify-between border-b border-[#2a2f38] bg-[#0f1217]/95 p-4 shadow-sm backdrop-blur lg:hidden">
          <Logo className="scale-75 origin-left" />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="text-slate-100 hover:bg-[#15191f]"
          >
            {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>

        <ErrorBoundary>
          {appRoutes}
        </ErrorBoundary>
      </main>

      {/* Floating AI Chat Assistant — hidden in the production pilot (DEV or flag only) */}
      {(R1_RELEASE_FLAGS.showAssistant || import.meta.env.DEV) && (
        <FloatingAIChat userName={user?.displayName || undefined} userRole={userProfile?.role} />
      )}
    </div>
  );
}

// Login screen component for /login route
function LoginScreen() {
  const { login } = useAppContext();
  const navigate = useNavigate();

  const handleLogin = async () => {
    console.log('DEBUG: Kliknięto logowanie');
    try {
      await login();
      console.log('DEBUG: Logowanie udane, przekierowanie do /app/wycena');
      navigate('/app/wycena');
    } catch (error) {
      console.error('DEBUG: Błąd logowania:', error);
    }
  };

  return (
    <div className="flex h-screen items-center justify-center bg-slate-50">
      <div className="text-center space-y-6 p-8 bg-white rounded-2xl shadow-sm max-w-md w-full border border-slate-100">
        <div className="flex justify-center">
          <Logo variant="dark" />
        </div>
        <div>
          <p className="text-slate-500 mt-2">Zaloguj się, aby uzyskać dostęp do swoich wycen i bazy wiedzy.</p>
        </div>
        <Button onClick={handleLogin} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg">
          Zaloguj się przez Google
        </Button>
        <Button variant="ghost" onClick={() => navigate('/')} className="w-full text-slate-400">
          Powrót do strony głównej
        </Button>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <Router>
          <SeoIndexGuard />
          <OfflineBanner />
          <PwaUpdatePrompt />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/kontakt" element={<ContactPage />} />
            <Route path="/o-produkcie" element={<AboutProductPage />} />
            <Route path="/tarcza-marzy" element={<MarginShieldPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/regulamin" element={<BetaTermsPage />} />
            <Route path="/polityka-prywatnosci" element={<BetaPrivacyPage />} />
            <Route path="/wycena" element={<PublicQuickEstimateRoute />} />
            <Route path="/login" element={<LoginScreen />} />
            <Route path="/regulamin-testow-beta" element={<BetaTermsPage />} />
            <Route path="/quote/:id" element={<PublicQuote />} />
            <Route path="/offer/:id" element={<ClientOffer />} />
            <Route path="/app/*" element={<AppContent />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AppProvider>
    </ErrorBoundary>
  );
}

